import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { PublicNav } from '../components/PublicNav';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  CheckCircle2, Loader2, ShieldCheck, ClipboardCheck, GraduationCap, Award,
  Calendar, Heart, ChevronRight, Phone,
} from 'lucide-react';
import { toast } from 'sonner';

const PROGRAM_OPTIONS = [
  { value: 'BOARD_AND_TRAIN', label: 'Board and Train' },
  { value: 'PRIVATE_LESSONS', label: 'Private Lessons' },
];
const REFERRAL_OPTIONS = [
  { value: 'GOOGLE', label: 'Google' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'WORD_OF_MOUTH', label: 'Word of Mouth' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'OTHER', label: 'Other' },
];

const BLANK = {
  first_name: '', last_name: '', email: '', phone: '',
  dog_name: '', dog_breed: '',
  behavioral_concerns: '',
  bite_history: '',
  medical_issues: '',
  program_interest: 'BOARD_AND_TRAIN',
  referral_source: '',
  sms_consent: false,
};

const ValueProp = ({ icon: Icon, title, desc, testid }) => (
  <Card className='p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800' data-testid={testid}>
    <div className='w-10 h-10 rounded-lg bg-[#1B3A87]/10 dark:bg-[#1B3A87]/30 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center mb-3'>
      <Icon className='w-5 h-5' />
    </div>
    <div className='font-display font-bold text-base text-[#0B1220] dark:text-slate-50'>{title}</div>
    <p className='text-sm text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed'>{desc}</p>
  </Card>
);

const ProgramCard = ({ duration, price, dogType, features, popular, testid }) => (
  <Card
    className={`relative p-6 border bg-white dark:bg-slate-900 ${
      popular ? 'border-[#C8102E] shadow-xl' : 'border-slate-200 dark:border-slate-800'
    }`}
    data-testid={testid}
  >
    {popular && (
      <Badge className='absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8102E] text-white border-0 px-3 py-1 font-bold uppercase tracking-wider text-[10px]'>
        Most Popular
      </Badge>
    )}
    <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#1B3A87] dark:text-[#93C5FD]'>Board & Train</div>
    <div className='font-display font-bold text-3xl text-[#0B1220] dark:text-slate-50 mt-1'>{duration}</div>
    <div className='text-xs text-slate-500 dark:text-slate-400 mt-1'>{dogType}</div>
    <div className='mt-4 flex items-baseline gap-1'>
      <span className='font-display font-bold text-4xl tabular-nums text-[#0B1220] dark:text-slate-50'>${price.toLocaleString()}</span>
    </div>
    <ul className='mt-5 space-y-2'>
      {features.map((f) => (
        <li key={f} className='flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200'>
          <CheckCircle2 className='w-4 h-4 text-[#167A5A] mt-0.5 shrink-0' /> {f}
        </li>
      ))}
    </ul>
  </Card>
);

const ProcessStep = ({ num, title, desc, testid }) => (
  <div className='flex gap-4' data-testid={testid}>
    <div className='shrink-0 w-12 h-12 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-display font-bold text-xl'>
      {num}
    </div>
    <div className='flex-1'>
      <div className='font-display font-bold text-lg text-[#0B1220] dark:text-slate-50'>{title}</div>
      <p className='text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed'>{desc}</p>
    </div>
  </div>
);

export default function BoardTrainForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onCheck = (k) => (v) => setForm({ ...form, [k]: !!v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referral_source) { toast.error('Tell us how you heard about us'); return; }
    setSubmitting(true);
    try {
      await api.post('/board-train-applications', form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className='min-h-screen bg-[#FFF8F2] dark:bg-slate-950'>
      <PublicNav />

      {/* Hero */}
      <section className='relative hero-bg noise overflow-hidden'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center'>
          <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E] mb-2'>Board & Train</div>
          <h1 className='font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 leading-[1.1] max-w-4xl mx-auto'>
            Start the <span className='text-[#C8102E]'>Board & Train Client Profile</span> here — as part of your website!
          </h1>
          <p className='mt-5 text-lg text-slate-700 dark:text-slate-200 max-w-3xl mx-auto leading-relaxed'>
            Capture every inquiry in one place. Prospective clients tell you about their dog, upload photos,
            and land in your K9os dashboard ready to convert — no more scattered forms, DMs, or missed leads.
          </p>
          <div className='mt-7 flex flex-wrap items-center gap-3 justify-center'>
            <Button
              size='lg'
              className='bg-[#C8102E] hover:bg-[#A50D26] text-white btn-shimmer h-12 px-6'
              onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid='hero-apply-button'
            >
              Fill out Application <ChevronRight className='w-4 h-4 ml-1' />
            </Button>
            <a
              href='tel:718-691-2711'
              className='inline-flex items-center gap-2 h-12 px-5 rounded-md border border-[#1B3A87]/30 dark:border-[#93C5FD]/40 text-[#1B3A87] dark:text-[#93C5FD] font-semibold hover:bg-[#1B3A87]/5 dark:hover:bg-[#1B3A87]/15'
              data-testid='hero-call-button'
            >
              <Phone className='w-4 h-4' /> (718) 691-2711
            </a>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className='py-12 sm:py-16 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
            <ValueProp icon={ClipboardCheck} title='Customized Training Plans' desc="Our certified trainers have years of experience working with all breeds and behavioral issues." testid='value-customized' />
            <ValueProp icon={GraduationCap} title='Proven K9 Training Methods' desc="We've successfully trained thousands of dogs, transforming challenging behaviors into obedient companions." testid='value-proven' />
            <ValueProp icon={Calendar} title='Flexible Programs' desc="Choose from 2 or 4-week programs based on your dog's specific training needs." testid='value-flexible' />
            <ValueProp icon={Heart} title='Real Client Success Stories' desc="We offer flexible payment plans to make professional training accessible for every family." testid='value-success' />
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id='apply' className='py-12 sm:py-16 bg-[#FFF8F2] dark:bg-slate-950'>
        <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-8'>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Application</div>
            <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 mt-1'>
              Start Your Dog's Transformation
            </h2>
            <p className='text-slate-600 dark:text-slate-300 mt-3'>
              Fill out the form below and we'll contact you to schedule a consultation and discuss the best program for your dog.
            </p>
          </div>

          <Card className='p-6 sm:p-8 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg' data-testid='board-train-form-card'>
            {done ? (
              <div className='py-10 text-center' data-testid='board-train-success'>
                <div className='w-16 h-16 rounded-full bg-[#167A5A]/10 text-[#167A5A] flex items-center justify-center mx-auto mb-4'>
                  <CheckCircle2 className='w-8 h-8' />
                </div>
                <h3 className='font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50'>Application received</h3>
                <p className='text-slate-600 dark:text-slate-300 mt-2 max-w-md mx-auto'>
                  Thanks {form.first_name || 'there'}! Our team will reach out within 24 hours to discuss
                  {' '}{form.dog_name ? `${form.dog_name}'s` : "your dog's"} training needs and confirm next steps.
                </p>
                <div className='mt-6 flex flex-wrap gap-3 justify-center'>
                  <Button className='bg-[#1B3A87] hover:bg-[#16306F] text-white' onClick={() => { setForm(BLANK); setDone(false); }} data-testid='submit-another-button'>
                    Submit another application
                  </Button>
                  <Button variant='outline' onClick={() => navigate('/')} data-testid='back-home-button'>
                    Back to home
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className='grid grid-cols-1 sm:grid-cols-2 gap-4' data-testid='board-train-form'>
                <div>
                  <Label htmlFor='first_name'>First Name *</Label>
                  <Input id='first_name' required value={form.first_name} onChange={onChange('first_name')} data-testid='input-first-name' />
                </div>
                <div>
                  <Label htmlFor='last_name'>Last Name *</Label>
                  <Input id='last_name' required value={form.last_name} onChange={onChange('last_name')} data-testid='input-last-name' />
                </div>
                <div>
                  <Label htmlFor='email'>Email Address *</Label>
                  <Input id='email' type='email' required value={form.email} onChange={onChange('email')} data-testid='input-email' />
                </div>
                <div>
                  <Label htmlFor='phone'>Phone Number *</Label>
                  <Input id='phone' type='tel' required value={form.phone} onChange={onChange('phone')} placeholder='(479) 555-0123' data-testid='input-phone' />
                </div>
                <div>
                  <Label htmlFor='dog_name'>Dog Name *</Label>
                  <Input id='dog_name' required value={form.dog_name} onChange={onChange('dog_name')} data-testid='input-dog-name' />
                </div>
                <div>
                  <Label htmlFor='dog_breed'>Dog Breed *</Label>
                  <Input id='dog_breed' required value={form.dog_breed} onChange={onChange('dog_breed')} placeholder='Belgian Malinois, German Shepherd…' data-testid='input-dog-breed' />
                </div>
                <div className='sm:col-span-2'>
                  <Label htmlFor='behavioral_concerns'>Behavioral Concerns *</Label>
                  <Textarea
                    id='behavioral_concerns'
                    required
                    rows={4}
                    value={form.behavioral_concerns}
                    onChange={onChange('behavioral_concerns')}
                    placeholder='e.g. leash reactivity, recall, jumping, resource guarding…'
                    data-testid='input-concerns'
                  />
                </div>
                <div className='sm:col-span-2'>
                  <Label htmlFor='bite_history'>Has your dog bitten in the past? If yes, please explain *</Label>
                  <Textarea
                    id='bite_history'
                    required
                    rows={3}
                    value={form.bite_history}
                    onChange={onChange('bite_history')}
                    placeholder="If no, simply type 'No'."
                    data-testid='input-bite-history'
                  />
                </div>
                <div className='sm:col-span-2'>
                  <Label htmlFor='medical_issues'>Does your dog have any medical issues or ailments? If yes, please explain *</Label>
                  <Textarea
                    id='medical_issues'
                    required
                    rows={3}
                    value={form.medical_issues}
                    onChange={onChange('medical_issues')}
                    placeholder="Allergies, medications, joint issues, etc. If none, type 'None'."
                    data-testid='input-medical'
                  />
                </div>
                <div>
                  <Label>Are you interested in Board and Train or Private Lessons? *</Label>
                  <Select value={form.program_interest} onValueChange={(v) => setForm({ ...form, program_interest: v })}>
                    <SelectTrigger className='mt-1.5' data-testid='input-program-interest'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROGRAM_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>How did you hear about us? *</Label>
                  <Select value={form.referral_source} onValueChange={(v) => setForm({ ...form, referral_source: v })}>
                    <SelectTrigger className='mt-1.5' data-testid='input-referral'><SelectValue placeholder='Select an option' /></SelectTrigger>
                    <SelectContent>
                      {REFERRAL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='sm:col-span-2 flex items-start gap-2 mt-2'>
                  <Checkbox
                    id='sms_consent'
                    checked={form.sms_consent}
                    onCheckedChange={onCheck('sms_consent')}
                    data-testid='input-sms-consent'
                    className='mt-0.5'
                  />
                  <Label htmlFor='sms_consent' className='text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-normal cursor-pointer'>
                    By checking this box, you agree to receive SMS messages from Joint Forces K9 related to
                    appointments and follow up messages. You may reply STOP to opt out at any time. Reply HELP
                    to (718) 691-2711 for assistance. Message and data rates may apply. Message frequency will vary.
                  </Label>
                </div>
                <div className='sm:col-span-2 mt-2'>
                  <Button
                    type='submit'
                    disabled={submitting}
                    className='w-full bg-[#C8102E] hover:bg-[#A50D26] text-white h-12 text-base font-semibold'
                    data-testid='submit-application-button'
                  >
                    {submitting ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : null}
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </Button>
                  <p className='text-[11px] text-slate-500 dark:text-slate-400 mt-3 text-center'>
                    By submitting, you agree to our terms and privacy policy.
                  </p>
                </div>
              </form>
            )}
          </Card>
        </div>
      </section>

      {/* Programs */}
      <section className='py-12 sm:py-16 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-10'>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Our Programs</div>
            <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 mt-1'>
              Board & Train Programs for Lasting Obedience
            </h2>
            <p className='text-slate-600 dark:text-slate-300 mt-3 max-w-2xl mx-auto'>
              All programs include comprehensive training, daily exercise, comfortable boarding, and
              handler training sessions to ensure you maintain your dog's new skills.
            </p>
          </div>
          <div className='grid md:grid-cols-2 gap-5 max-w-4xl mx-auto' data-testid='programs-grid'>
            <ProgramCard
              duration='2 Weeks'
              dogType='Dogs needing basic obedience and manners'
              price={1900}
              features={[
                'Foundation obedience training',
                'Basic commands (sit, stay, come, heel)',
                'Leash walking skills',
                'Socialization basics',
                'Progress updates with photos/videos',
                '2 follow-up training sessions with you',
              ]}
              testid='program-2-week'
            />
            <ProgramCard
              duration='4 Weeks'
              dogType='Dogs with serious behavioral challenges'
              price={3800}
              popular
              features={[
                'Everything in 2-week program',
                'Complex behavioral modification',
                'Advanced off-leash reliability',
                'Real-world scenario training',
                'Aggression management (if applicable)',
                '3 follow-up training sessions with you',
              ]}
              testid='program-4-week'
            />
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className='py-12 sm:py-16 bg-[#FFF8F2] dark:bg-slate-950'>
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-10'>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>The Process</div>
            <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 mt-1'>
              What to Expect During Board & Train
            </h2>
          </div>
          <div className='grid sm:grid-cols-2 gap-6 sm:gap-8'>
            <ProcessStep num={1} title='Initial Consultation' desc="We assess your dog's needs and create a customized training plan." testid='step-1' />
            <ProcessStep num={2} title='Drop-Off Day' desc="Bring your dog to our facility where they'll meet their trainer and settle in." testid='step-2' />
            <ProcessStep num={3} title='Intensive Training' desc='Daily one-on-one training sessions with regular progress updates sent to you.' testid='step-3' />
            <ProcessStep num={4} title='Go-Home Day' desc="Learn how to maintain your dog's new skills with hands-on training sessions." testid='step-4' />
          </div>
          <div className='mt-12 flex flex-wrap gap-3 justify-center'>
            <Button
              size='lg'
              onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })}
              className='bg-[#1B3A87] hover:bg-[#16306F] text-white h-12 px-6'
              data-testid='process-apply-cta'
            >
              <Award className='w-4 h-4 mr-1.5' /> Reserve Your Dog's Spot
            </Button>
            <Button
              size='lg'
              variant='outline'
              onClick={() => navigate('/#financing')}
              className='h-12 px-6 border-[#1B3A87]/30 text-[#1B3A87] dark:text-[#93C5FD] dark:border-[#93C5FD]/40 hover:bg-[#1B3A87]/5 dark:hover:bg-[#1B3A87]/10'
              data-testid='process-financing-cta'
            >
              <ShieldCheck className='w-4 h-4 mr-1.5' /> Explore Financing
            </Button>
          </div>
        </div>
      </section>

      <footer className='py-10 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-slate-500 dark:text-slate-400 text-center'>
          © {new Date().getFullYear()} K9os.app · All rights reserved.
        </div>
      </footer>
    </div>
  );
}
